import {Award, CheckCircle, Clock, ExternalLink, Shield, Star, TrendingUp} from 'lucide-react'
import { Link } from "react-router-dom"
import Breadcrumbs from "@/components/seo/Breadcrumbs"
import JsonLd from "@/components/seo/JsonLd"
import MedicalDisclaimer from "@/components/seo/MedicalDisclaimer"
import SeoHead, { SITE_NAME, SITE_URL } from "@/components/seo/SeoHead"
import { getRelatedReviews, type ReviewProduct } from "@/data/reviews"

interface ReviewPageTemplateProps {
  product: ReviewProduct
}

function ReviewPageTemplate({ product }: ReviewPageTemplateProps) {
  const related = getRelatedReviews(product)
  const path = `/reviews/${product.slug}`
  const url = `${SITE_URL}${path}`

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Top Reviews", item: `${SITE_URL}/reviews` },
        { "@type": "ListItem", position: 3, name: `${product.name} Review`, item: url }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "Review",
      name: `${product.name} Review`,
      url,
      reviewBody: product.overview,
      reviewRating: {
        "@type": "Rating",
        ratingValue: product.rating,
        bestRating: 5
      },
      author: {
        "@type": "Organization",
        name: SITE_NAME
      },
      itemReviewed: {
        "@type": "Product",
        name: product.name,
        image: product.image,
        description: product.tagline,
        brand: {
          "@type": "Brand",
          name: "Vitamin Vital"
        },
        offers: {
          "@type": "Offer",
          price: product.price.replace("$", ""),
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
          url: product.affiliateUrl === "#" ? url : product.affiliateUrl
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: product.rating,
          reviewCount: product.reviews,
          bestRating: 5
        }
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: product.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer
        }
      }))
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-teal-50">
      <SeoHead title={product.seoTitle} description={product.seoDescription} path={path} />
      <JsonLd data={schema} />

      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6">
            <Award className="w-5 h-5" />
            <span className="text-sm font-medium">Review & Dosage Guide</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{product.name} Review</h1>
          <p className="text-xl text-teal-100 max-w-3xl">{product.tagline}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs
          items={[
            { label: "Home", href: "/" },
            { label: "Top Reviews", href: "/reviews" },
            { label: `${product.name} Review` }
          ]}
        />

        <article className="bg-white rounded-2xl shadow-lg overflow-hidden mb-12">
          <div className="grid md:grid-cols-2 gap-8 p-8">
            <div>
              <div className="relative rounded-xl overflow-hidden mb-6 bg-gradient-to-br from-teal-50 to-orange-50 p-8">
                <img
                  src={product.image}
                  alt={`${product.name} bottle`}
                  className="w-full h-64 object-cover rounded-lg"
                />
                <div className="absolute top-4 right-4 bg-orange-500 text-white px-4 py-2 rounded-full font-bold text-sm">
                  Editor's Choice
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < Math.floor(product.rating)
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  ))}
                </div>
                <span className="font-semibold text-gray-900">{product.rating}</span>
                <span className="text-gray-500">({product.reviews} reviews)</span>
              </div>

              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Best Price</div>
                  <div className="text-3xl font-bold text-teal-600">{product.price}</div>
                </div>
                <a
                  href={product.affiliateUrl}
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-semibold transition-colors"
                >
                  Check Best Price
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div>
              <p className="text-lg text-gray-600 mb-6">{product.overview}</p>

              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-teal-600" />
                  Key Benefits
                </h2>
                <ul className="space-y-2">
                  {product.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-teal-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-teal-600" />
                  Quality Features
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {product.keyFeatures.map((feature) => (
                    <div key={feature} className="bg-teal-50 rounded-lg px-3 py-2 text-sm text-gray-700">
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </article>

        <section className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-6 h-6 text-teal-600" />
              {product.name} Dosage Guide
            </h2>
            <p className="text-gray-700 mb-3">
              <strong>Typical dose:</strong> {product.dosage.amount}
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Best time:</strong> {product.dosage.timing}
            </p>
            <ul className="space-y-2">
              {product.dosage.notes.map((note) => (
                <li key={note} className="flex items-start gap-2 text-sm text-gray-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-2 flex-shrink-0" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Who It Is For</h2>
            <ul className="space-y-2 mb-6">
              {product.whoItsFor.map((item) => (
                <li key={item} className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="w-4 h-4 text-teal-600 mt-1 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Who should be cautious</h3>
            <ul className="space-y-2">
              {product.whoShouldAvoid.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {product.faqs.map((faq) => (
              <div key={faq.question} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Reviews & Guides</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((item) => (
              <Link
                key={item.slug}
                to={`/reviews/${item.slug}`}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <div className="text-sm text-teal-700 font-medium mb-2">{item.category}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.name} Review</h3>
                <p className="text-sm text-gray-600">{item.tagline}</p>
              </Link>
            ))}
            {product.relatedComparisonSlug ? (
              <Link
                to={`/comparison/${product.relatedComparisonSlug}`}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <div className="text-sm text-orange-700 font-medium mb-2">Comparison Guide</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Arko Vital vs. B Vital</h3>
                <p className="text-sm text-gray-600">See which energy formula is the better match.</p>
              </Link>
            ) : null}
          </div>
        </section>

        <div className="flex flex-col sm:flex-row gap-4 mb-12">
          <a
            href={product.affiliateUrl}
            className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-full font-semibold transition-colors"
          >
            Check Best Price
            <ExternalLink className="w-4 h-4" />
          </a>
          <Link
            to="/calculator"
            className="inline-flex items-center justify-center px-8 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-semibold transition-colors"
          >
            Take the Vitality Quiz
          </Link>
        </div>

        <MedicalDisclaimer extra="Vitamin Vital Portal may earn affiliate commissions from purchases made through links on this page." />
      </div>
    </div>
  )
}

export default ReviewPageTemplate
